import React, { useState, useEffect, useRef } from 'react';
import { LoaderCircle, ArrowLeft, Terminal, GitBranch, Github, Gitlab, Package, CheckCircle2, Globe, AlertCircle, Check, X, XCircle, Clock, Lock, Rocket, Wand2, Code, Settings, FileUp, Search, Server, ExternalLink, Cpu, HardDrive, Box } from 'lucide-react';
import api from '../../services/api';
import '../../styles/premium-deploy-progress.css';

// "https://github.com/owner/repo(.git)" -> "owner/repo"
function parseRepoFullName(urlStr) {
  try {
    if (!urlStr) return '';
    const u = new URL(String(urlStr));
    const parts = u.pathname.split('/').filter(Boolean);
    if (parts.length < 2) return '';
    let repo = parts[parts.length - 1];
    if (repo.toLowerCase().endsWith('.git')) repo = repo.slice(0, -4);
    return `${parts[parts.length - 2]}/${repo}`;
  } catch { return ''; }
}

function formatDeployDuration(ms) {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

const GIT_CONFIGURE_DRAFT_KEY = 'devorants.git.configureDraft';

function readGitConfigureDraft() {
  try {
    const raw = sessionStorage.getItem(GIT_CONFIGURE_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || !draft.fetchedRepoData) return null;
    return draft;
  } catch {
    return null;
  }
}

function writeGitConfigureDraft(draft) {
  try {
    if (!draft || !draft.fetchedRepoData) {
      sessionStorage.removeItem(GIT_CONFIGURE_DRAFT_KEY);
      return;
    }
    sessionStorage.setItem(GIT_CONFIGURE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* ignore quota / private mode */
  }
}

export default function GitRepoDeploy({
  user, onStatusMessage, onBack, api,
  beginDeploymentTracking, refreshDeploymentSession, setCurrentSessionProgress, onTabChange, activeTab, setActiveTab,
  openComposeWorkspace, closeComposeWorkspace, deploymentSession,
  workspaceMinimized, restoreWorkspace
}) {
  const [repoAppName, setRepoAppName] = useState('');
  const [repoAppNameError, setRepoAppNameError] = useState('');
  const [repoUrl, setRepoUrl] = useState('');
  const [repoBranch, setRepoBranch] = useState('main');
  const [repoBuildOutputDir, setRepoBuildOutputDir] = useState('');
  const [repoInstallCommand, setRepoInstallCommand] = useState('');
  const [repoBuildCommand, setRepoBuildCommand] = useState('');
  const [repoFramework, setRepoFramework] = useState('auto');
  const [detectingRepoId, setDetectingRepoId] = useState(null);
  const [repoRootDirectory, setRepoRootDirectory] = useState('./');

  const validateRepoAppName = (val) => {
    if (!val) {
      setRepoAppNameError('');
      return false;
    }
    if (/\s/.test(val)) {
      setRepoAppNameError('Spaces are not allowed. Use hyphens (-) instead.');
      return false;
    }
    if (/[A-Z]/.test(val)) {
      setRepoAppNameError('Uppercase letters are not allowed. Use lowercase only.');
      return false;
    }
    if (!/^[a-z0-9-]+$/.test(val)) {
      setRepoAppNameError('Only lowercase letters, numbers, and hyphens (-) are allowed.');
      return false;
    }
    setRepoAppNameError('');
    return true;
  };

  const handleRepoAppNameChange = (e) => {
    const val = e.target.value;
    setRepoAppName(val);
    validateRepoAppName(val);
  };
  
  const [gitProviders, setGitProviders] = useState(() => {
    try {
      const saved = localStorage.getItem('aiBitGitProviders');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [gitProvider, setGitProvider] = useState('github');
  const [gitRepos, setGitRepos] = useState([]);
  const [gitSearch, setGitSearch] = useState('');
  const [gitReposLoading, setGitReposLoading] = useState(false);
  const [connectError, setConnectError] = useState('');
  
  const [fetchedRepoData, setFetchedRepoData] = useState(null);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  
  const [repoEnvVars, setRepoEnvVars] = useState([]); 
  const [repoRawEnv, setRepoRawEnv] = useState(''); 
  const [envMode, setEnvMode] = useState('ui'); 
  const [envKey, setEnvKey] = useState('');
  const [envValue, setEnvValue] = useState('');
  const [repoAdditionalFiles, setRepoAdditionalFiles] = useState([]);
  
  const [deployedUrl, setDeployedUrl] = useState('');
  const [deployStep, setDeployStep] = useState(0); 
  const [deployError, setDeployError] = useState('');
  const [deployErrorCode, setDeployErrorCode] = useState(null);
  const [deployErrorHint, setDeployErrorHint] = useState('');
  const [buildLogContent, setBuildLogContent] = useState('');
  const [loadingBuildLog, setLoadingBuildLog] = useState(false);
  const [deploymentSessionId, setDeploymentSessionId] = useState(null);
  // What the completed success screen should show (static leftover label was wrong for workspaces).
  const [lastDeployKind, setLastDeployKind] = useState('backend-workspace'); // 'static' | 'backend-workspace' | 'compose' | 'ssr'
  const [lastDeployDomain, setLastDeployDomain] = useState('');
  
  const [repoMode, setRepoMode] = useState('connect'); 
  const [cancelLoading, setCancelLoading] = useState(false);
  
  // Backend / workspace detection
  const [backendDetected, setBackendDetected] = useState(false);
  const [ssrDetected, setSsrDetected] = useState(false);
  const [backendSuggestedImage, setBackendSuggestedImage] = useState('alma-node');
  const [backendPort, setBackendPort] = useState(3000);
  const [workspaceCpu, setWorkspaceCpu] = useState('1.0');
  const [workspaceMemory, setWorkspaceMemory] = useState('1024m');
  const [repoEntryPoints, setRepoEntryPoints] = useState([]);
  const [isScanningEntryPoints, setIsScanningEntryPoints] = useState(false);
  const [lastDeployResult, setLastDeployResult] = useState(null);
  const [deployStartedAt, setDeployStartedAt] = useState(null);
  const [deployElapsedMs, setDeployElapsedMs] = useState(0);
  const [deployDurationMs, setDeployDurationMs] = useState(null);
  const deployStartedAtRef = useRef(null);
  const lastDeployResultRef = useRef(null);
  const [workspaceVerified, setWorkspaceVerified] = useState(false);

  // Docker Compose detection (set when the picked repo has a docker-compose file)
  const [composeDetected, setComposeDetected] = useState(false);
  const [composeInfo, setComposeInfo] = useState(null); // { composeFile, services[], suggestedEntryService, suggestedInternalPort }
  const [composeEntryService, setComposeEntryService] = useState('web');
  const [composeInternalPort, setComposeInternalPort] = useState(80);
  const [exposedPortsStr, setExposedPortsStr] = useState('');
  const [deployModeOverride, setDeployModeOverride] = useState('auto'); // 'auto' | 'compose' | 'backend' | 'ssr'

  const [reposFetchError, setReposFetchError] = useState('');
  const pollIntervalRef = useRef(null);
  const gitProvidersRef = useRef(gitProviders);
  gitProvidersRef.current = gitProviders;
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const stillRunning = activeTab === 'repo-deploying'
      && !deployError
      && Boolean(deployStartedAt)
      && !workspaceVerified
      && deployDurationMs == null;
    if (!stillRunning) return undefined;
    const tick = () => setDeployElapsedMs(Date.now() - deployStartedAt);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeTab, deployError, deployStartedAt, deployDurationMs, workspaceVerified]);

  // Sync provider connection status from backend on mount.
  // Backend is the source of truth (tokens are stored server-side);
  // localStorage can hold stale "connected" flags that cause silent failures.
  useEffect(() => {
    const syncProviderStatus = async () => {
      try {
        const status = await api.getGitProviderStatus();
        if (!mountedRef.current || !status) return;
        const synced = {
          github: Boolean(status.providers?.github),
          gitlab: Boolean(status.providers?.gitlab)
        };
        // Always update from backend when it responds with providers data
        // (even if both are false) to clear stale localStorage flags
        if (status.providers) {
          setGitProviders((prev) => {
            const merged = { ...prev, ...synced };
            localStorage.setItem('aiBitGitProviders', JSON.stringify(merged));
            return merged;
          });
          // Prefer a connected provider if the current one is not
          if (synced.github || synced.gitlab) {
            setGitProvider((current) => {
              if (synced[current]) return current;
              return synced.github ? 'github' : 'gitlab';
            });
          }
        }
      } catch (err) {
        // Failed status sync should not break the UI; fall back to localStorage
        console.warn('[GitRepoDeploy] Could not sync git provider status:', err.message);
      }
    };
    syncProviderStatus();
  }, []);

  useEffect(() => {
    if (gitProviders[gitProvider]) {
      fetchGitRepos(gitProvider);
    } else {
      setGitRepos([]);
    }
  }, [gitProvider, gitProviders]);

  useEffect(() => {
    if (activeTab === 'repo') setRepoMode('connect');
  }, [activeTab]);

  // Reload / cold start: restore configure draft, or leave the blank configure screen.
  useEffect(() => {
    if (activeTab !== 'repo-configure' && activeTab !== 'repo-deploying') return;
    if (fetchedRepoData) return;

    const draft = readGitConfigureDraft();
    if (draft && draft.fetchedRepoData) {
      setFetchedRepoData(draft.fetchedRepoData);
      if (draft.repoAppName) setRepoAppName(draft.repoAppName);
      if (draft.repoFramework) setRepoFramework(draft.repoFramework);
      if (draft.repoRootDirectory != null) setRepoRootDirectory(draft.repoRootDirectory);
      if (draft.gitProvider) setGitProvider(draft.gitProvider);
      if (draft.fetchedRepoData?.branch) setRepoBranch(draft.fetchedRepoData.branch);
      else if (draft.repoBranch) setRepoBranch(draft.repoBranch);
      setBackendDetected(Boolean(draft.backendDetected));
      setSsrDetected(Boolean(draft.ssrDetected || draft.fetchedRepoData?.isSSR));
      if (draft.backendSuggestedImage) setBackendSuggestedImage(draft.backendSuggestedImage);
      if (draft.backendPort) setBackendPort(Number(draft.backendPort) || 3000);
      if (draft.workspaceCpu) setWorkspaceCpu(draft.workspaceCpu);
      if (draft.workspaceMemory) setWorkspaceMemory(draft.workspaceMemory);
      setComposeDetected(Boolean(draft.composeDetected));
      setComposeInfo(draft.composeInfo || null);
      if (draft.composeEntryService) setComposeEntryService(draft.composeEntryService);
      if (draft.composeInternalPort) setComposeInternalPort(Number(draft.composeInternalPort) || 80);
      if (Array.isArray(draft.repoEnvVars)) setRepoEnvVars(draft.repoEnvVars);
      if (typeof draft.repoRawEnv === 'string') setRepoRawEnv(draft.repoRawEnv);
      if (Array.isArray(draft.repoAdditionalFiles)) setRepoAdditionalFiles(draft.repoAdditionalFiles);
      // Mid-deploy progress can't be reconstructed reliably after a hard reload.
      if (activeTab === 'repo-deploying') {
        setActiveTab('repo-configure');
        if (onTabChange) onTabChange('repo-configure');
      }
      return;
    }

    setActiveTab('repo');
    if (onTabChange) onTabChange('repo');
    // onTabChange is recreated each parent render; only react to tab / data gaps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fetchedRepoData]);

  // Keep draft updated while configuring so reload restores the form.
  useEffect(() => {
    if (activeTab !== 'repo-configure' && activeTab !== 'repo-deploying') return;
    if (!fetchedRepoData) return;
    writeGitConfigureDraft({
      fetchedRepoData,
      repoAppName,
      repoFramework,
      repoRootDirectory,
      repoBranch,
      gitProvider,
      backendDetected,
      ssrDetected,
      backendSuggestedImage,
      backendPort,
      workspaceCpu,
      workspaceMemory,
      composeDetected,
      composeInfo,
      composeEntryService,
      composeInternalPort,
      repoEnvVars,
      repoRawEnv,
      repoAdditionalFiles
    });
  }, [
    activeTab, fetchedRepoData, repoAppName, repoFramework, repoRootDirectory, gitProvider,
    backendDetected, ssrDetected, backendSuggestedImage, backendPort, workspaceCpu, workspaceMemory,
    composeDetected, composeInfo, composeEntryService, composeInternalPort, repoEnvVars, repoRawEnv, repoAdditionalFiles
  ]);

  useEffect(() => {
    const onOAuthMessage = (event) => {
      const data = event?.data;
      if (!data || data.type !== 'git-oauth-result') return;

      if (data.success) {
        setConnectError('');
        const provider = data.provider;
        
        // Auto-chain: right after OAuth success, open the GitHub App installation
        // once, in the SAME popup window (loading HTML in window.open is fine here;
        // the user already allowed the popup chain when clicking the parent button).
        if (provider === 'github' && !localStorage.getItem('aiBitGithubAppInstalled')) {
          localStorage.setItem('aiBitGithubAppInstalled', 'pending');
          api.getGithubSetupUrl().then(({ setupUrl }) => {
            if (setupUrl) {
              const samePopup = window.open(setupUrl, 'git-oauth-popup', 'width=680,height=760');
              if (!samePopup) {
                // Fallback: open a new tab if the popup was blocked
                window.open(setupUrl, '_blank', 'noopener,noreferrer');
              }
            }
          }).catch(() => {});
        }

        setGitProviders((prev) => {
          const next = { ...prev, [provider]: true };
          localStorage.setItem('aiBitGitProviders', JSON.stringify(next));
          return next;
        });
        setGitProvider(provider);
      } else {
        setConnectError(`Failed to connect ${data.provider}: ${data.message || 'Unknown error'}`);
      }
    };

    window.addEventListener('message', onOAuthMessage);
    return () => window.removeEventListener('message', onOAuthMessage);
  }, []);

  const handleConnectGitProvider = async (provider) => {
    setConnectError('');
    try {
      // Standard OAuth popup flow for all providers
      const data = await api.connectGitProvider(provider);
      if (data && data.authUrl) {
        const popup = window.open(data.authUrl, 'git-oauth-popup', 'width=680,height=760');
        if (!popup) {
          setConnectError('Popup blocked. Please allow popups and try again.');
        }
      }
    } catch (err) {
      setConnectError(`Failed to connect ${provider}: ${err.message}`);
    }
  };

  const handleManageGithubApp = async () => {
    try {
      const data = await api.getGithubAppInstallUrl();
      if (data && data.installUrl) {
        window.open(data.installUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      onStatusMessage(`Failed to open GitHub App settings: ${err.message}`, 'error');
    }
  };

  const handleDisconnectGitProvider = async () => {
    try {
      await api.disconnectGitProvider(gitProvider);
      const newProviders = { ...gitProviders };
      delete newProviders[gitProvider];
      setGitProviders(newProviders);
      localStorage.setItem('aiBitGitProviders', JSON.stringify(newProviders));
      setGitRepos([]);
    } catch (err) {
      onStatusMessage(`Failed to disconnect ${gitProvider}: ${err.message}`, 'error');
    }
  };

  const fetchGitRepos = async (provider) => {
    if (!gitProvidersRef.current[provider]) return;
    
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    setGitReposLoading(true);
    setReposFetchError('');
    try {
      const data = await api.getGitRepos(provider, { signal: abortControllerRef.current.signal });
      if (!mountedRef.current) return;
      const repos = Array.isArray(data) ? data : (data && Array.isArray(data.repos) ? data.repos : []);
      setGitRepos(repos);
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!mountedRef.current) return;
      console.error(err);
      setGitRepos([]);
      setReposFetchError(err.message || `Failed to load ${provider} repositories`);
      if (err.message?.includes('token') || err.status === 401 || err.message?.includes('invalid') || err.message?.includes('expired') || err.message?.includes('not connected')) {
        const newProviders = { ...gitProvidersRef.current };
        delete newProviders[provider];
        setGitProviders(newProviders);
        localStorage.setItem('aiBitGitProviders', JSON.stringify(newProviders));
      }
    } finally {
      if (mountedRef.current) {
        setGitReposLoading(false);
      }
    }
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 30) return `${diffDays}d ago`;
    return `${Math.floor(diffDays / 30)}mo ago`;
  };

  const handleSelectRepo = async (repo) => {
    setDetectingRepoId(repo.id);
    let detectedFramework = 'auto';
    const owner = repo.owner || '';
    const repoSlug = repo.path || (repo.fullName ? repo.fullName.split('/').pop() : (repo.name || ''));
    const repoName = repoSlug || repo.name || '';
    const branch = String(repo.defaultBranch || '').trim() || '';
    const extraOpts = { fullName: repo.fullName, projectId: repo.id };
    let suggestedImage = 'alma-node';
    let detectedIsSSR = false;
    let info = null;
    try {
      info = await api.detectRepoFramework(gitProvider, owner, repoName, branch || 'HEAD', extraOpts);
      if (info && info.framework) {
        detectedFramework = info.framework;
        detectedIsSSR = Boolean(info.isSSR);
        // Prefer AI Workspace unless detection clearly identified a static-only SPA.
        // Unknown/auto/soft-fail must NOT default to static (that hid solid backends).
        const knownStaticSpa = new Set([
          'react', 'vue', 'vite', 'svelte', 'angular', 'gatsby', 'eleventy', 'astro', 'nextjs', 'nuxtjs'
        ]);
        const fwName = String(info.framework || '').toLowerCase();
        const clearlyStaticSpa =
          knownStaticSpa.has(fwName) &&
          !Boolean(info.isBackend) &&
          !detectedIsSSR;
        setBackendDetected(!clearlyStaticSpa);
        setSsrDetected(detectedIsSSR);
        if (info.suggestedImage) {
          suggestedImage = info.suggestedImage;
          setBackendSuggestedImage(info.suggestedImage);
        } else if (!clearlyStaticSpa) {
          suggestedImage = 'alma-dev-workspace';
          setBackendSuggestedImage('alma-dev-workspace');
        } else {
          setBackendSuggestedImage('alma-node');
        }
        if (info.suggestedPort) {
          setBackendPort(Number(info.suggestedPort) || 3000);
        }
      } else {
        setBackendDetected(true);
        setSsrDetected(false);
      }
    } catch (err) {
      console.warn('Framework detection failed:', err);
      setBackendDetected(true);
      setSsrDetected(false);
    }

    // Docker Compose detection: if this repo has a docker-compose.yml, offer the
    // dedicated compose deployment path instead of the frontend build pipeline.
    let composeConfig = null;
    try {
      composeConfig = await api.detectComposeConfig(gitProvider, owner, repoName, branch, './', extraOpts);
    } catch (e) {
      console.warn('Compose detection failed:', e);
    }
    const hasCompose = Boolean(composeConfig && composeConfig.hasCompose);
    setComposeDetected(hasCompose);
    setComposeInfo(hasCompose ? composeConfig : (composeConfig && composeConfig.composeFile ? composeConfig : null));
    
    let allPorts = [];
    if (info && info.suggestedPort) {
      allPorts.push(Number(info.suggestedPort) || 3000);
    } else {
      allPorts.push(3000);
    }

    if (hasCompose) {
      setComposeEntryService(composeConfig.suggestedEntryService || 'web');
      setComposeInternalPort(composeConfig.suggestedInternalPort || 80);
      
      if (composeConfig.services) {
        composeConfig.services.forEach(s => {
          if (s.ports && s.ports.length > 0) {
            s.ports.forEach(p => {
              const portStr = String(p).split(':').pop();
              if (portStr && !allPorts.includes(Number(portStr))) {
                allPorts.push(Number(portStr));
              }
            });
          }
        });
      }
      if (composeConfig.suggestedInternalPort && !allPorts.includes(Number(composeConfig.suggestedInternalPort))) {
        allPorts.push(Number(composeConfig.suggestedInternalPort));
      }
    }
    
    setExposedPortsStr(allPorts.join(', '));
    setDeployModeOverride('auto');

    setDetectingRepoId(null);
    setRepoFramework(detectedFramework);
    const name = repoSlug || repo.name;
    const cleanAppName = name.replace(/[^a-zA-Z0-9-]/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'my-app';
    setRepoAppName(cleanAppName);
    const nextRepoData = {
      id: repo.id,
      name: name,
      owner: owner,
      appName: cleanAppName,
      repoUrl: repo.cloneUrl || '',
      branch: branch || (gitProvider === 'gitlab' ? 'master' : 'main'),
      isSSR: detectedIsSSR,
      nestedBackend: info?.nestedBackend || null,
      suggestedImage
    };
    setFetchedRepoData(nextRepoData);
    setRepoBranch(nextRepoData.branch);
    setRepoRootDirectory('./');
    setBackendSuggestedImage(suggestedImage);
    // Always show the configure form first so the user can set internal port,
    // runtime image, and env vars before the workspace opens.
    setActiveTab('repo-configure');
    if (onTabChange) onTabChange('repo-configure');
  };

  const buildWorkspaceInfo = (appName, result, session, isCompose) => {
    const payload = result || session || {};
    const ssh = payload.ssh || {};
    const rawUrl = session?.url || payload.url || '';
    const domain = payload.domain || ssh.domain || String(rawUrl).replace(/^https?:\/\//, '').replace(/\/$/, '');
    return {
      domain: domain || null,
      port: payload.port || null,
      sshPort: ssh.sshPort || payload.sshPort || payload.port || null,
      containerId: ssh.containerId || payload.runnerContainer || payload.containerName || appName,
      basePath: ssh.basePath || (isCompose ? '/app' : '/home/app'),
      extraPorts: payload.extraPorts || null,
      deploymentType: isCompose ? 'compose' : 'backend-workspace',
      initialTab: 'ai',
      autoDeploy: false,
      isDeploying: false,
      appReady: Boolean(payload.appReady || (payload.autoStart && payload.autoStart.listening))
    };
  };


  const startGitWorkspaceDeploy = async ({
    appName,
    repoUrl,
    branch = 'main',
    rootDir = './',
    isCompose = false,
    isSSR = false,
    isStatic = false,
    composeEntry = 'web',
    composePort = 80,
    image = 'alma-node',
    port = 3000,
    cpus = '1.0',
    memory = '1024m',
    envVars = [],
    additionalFiles = []
  }) => {
    if (!appName) {
      onStatusMessage('Please provide an Application Name', 'error');
      return;
    }
    if (!repoUrl) {
      onStatusMessage('Repository URL is missing', 'error');
      return;
    }
    if (typeof openComposeWorkspace !== 'function') {
      onStatusMessage('Workspace UI is unavailable', 'error');
      return;
    }

    setRebuildLoading(true);
    setDeployError('');
    setDeployErrorCode(null);
    setDeployErrorHint('');
    setDeployStep(0);
    setDeployedUrl('');
    setLastDeployDomain('');
    setLastDeployResult(null);
    lastDeployResultRef.current = null;
    setWorkspaceVerified(false);
    const started = Date.now();
    deployStartedAtRef.current = started;
    setDeployStartedAt(started);
    setDeployElapsedMs(0);
    setDeployDurationMs(null);
    setLastDeployKind(isCompose ? 'compose' : (isSSR ? 'ssr' : (isStatic ? 'repo' : 'backend-workspace')));
    
    if (isStatic) {
      setActiveTab('repo-deploying');
    } else {
      // For AI Workspace deployments, we keep the background on the configure form
      // so the user isn't shown redundant/confusing loading screens behind the modal.
    }

    const deploymentType = isCompose ? 'compose' : (isStatic ? 'repo' : 'backend-workspace');
    const basePath = isCompose ? '/app' : '/home/app';

    const finishFromWorkspace = (payload = {}) => {
      const finishedAt = Date.now();
      const duration = finishedAt - (deployStartedAtRef.current || finishedAt);
      setDeployDurationMs(duration);
      setDeployElapsedMs(duration);
      setRebuildLoading(false);
      const prior = lastDeployResultRef.current || {};
      const verification = payload.verification || prior.verification || null;
      const result = {
        ...prior,
        ...(payload.result || {}),
        domain: payload.domain || prior.domain || '',
        url: payload.url || prior.url || '',
        publicUrls: payload.publicUrls || verification?.publicUrls || prior.publicUrls || [],
        // Prefer AI workspace / agent verification apps & bindings for dashboards.
        bindings: payload.bindings || verification?.bindings || prior.bindings || [],
        apps: payload.apps || verification?.apps || prior.apps || [],
        port: payload.port != null ? payload.port : prior.port,
        internalPort: payload.internalPort || prior.internalPort || port,
        cpus: payload.cpus || prior.cpus || cpus,
        memory: payload.memory || prior.memory || memory,
        image: payload.image || prior.image || image,
        containerName: payload.containerName || prior.containerName || null,
        verification
      };
      if (!result.url && result.domain) result.url = `http://${result.domain}/`;
      lastDeployResultRef.current = result;
      if (result.domain) setLastDeployDomain(result.domain);
      if (result.url) setDeployedUrl(result.url);
      setLastDeployResult(result);
      setDeployStep(4);
      setWorkspaceVerified(true);
      // Reveal the success page under the closing workspace modal.
      setActiveTab('repo-deploying');
      if (onTabChange) onTabChange('repo-deploying');
      onStatusMessage('Deployment successful — details below', 'success');
    };

    // Open workspace immediately after the configure form so the user sees progress.
    if (!isStatic) {
      openComposeWorkspace(appName, {
        isDeploying: true,
        deploymentType,
        basePath,
        autoDeploy: true,
        initialTab: 'ai',
        isSSR: Boolean(isSSR),
        internalPort: port,
        cpus,
        memory,
        image,
        deployStartedAt: started,
        onAiVerified: (payload) => {
          finishFromWorkspace({
            ...payload,
            internalPort: port,
            cpus,
            memory,
            image
          });
        }
      });
    }

    const deploymentId = beginDeploymentTracking(isCompose ? 'compose' : (isSSR ? 'ssr' : (isStatic ? 'repo' : 'backend-workspace')), appName);
    setDeploymentSessionId(deploymentId);

    // Start polling immediately so the provisioning overlay shows live milestones/logs
    // while the backend clones and starts the container in the background.
    pollDeploymentStatus(deploymentId, { isCompose, isSSR, isStaticDeploy: isStatic, appName, cpus, memory, image, port });

    try {
      let response;
      if (isStatic) {
        response = await api.deployGitRepo(appName, {
            repoUrl,
            branch,
            provider: gitProvider,
            rootDir,
            deploymentId,
            envVars,
            additionalFiles
        });
      } else if (isCompose) {
        response = await api.deployComposeRepo(appName, {
            repoUrl,
            branch,
            rootDir,
            entryService: composeEntry,
            internalPort: composePort,
            deploymentId,
            envVars,
            additionalFiles,
            cpu: cpus,
            memory
        });
      } else {
        response = await api.deployBackendRepo(appName, {
            repoUrl,
            branch,
            provider: gitProvider,
            rootDir,
            image,
            port,
            cpus,
            memory,
            deploymentId,
            envVars,
            additionalFiles
        });
      }

      if (!mountedRef.current) return;

      const backendDeploymentId = response?.deploymentId;
      const pollId = backendDeploymentId || deploymentId;
      if (backendDeploymentId && backendDeploymentId !== deploymentId) {
        setDeploymentSessionId(backendDeploymentId);
        pollDeploymentStatus(pollId, { isCompose, isSSR, isStaticDeploy: isStatic, appName, cpus, memory, image, port });
      }

      if (response && (response.port || response.ssh || response.domain || response.containerName)) {
        const resultMeta = {
          ...response,
          cpus: response.cpus || cpus,
          memory: response.memory || memory,
          image: response.image || image,
          internalPort: response.internalPort || port
        };
        lastDeployResultRef.current = resultMeta;
        setLastDeployResult(resultMeta);
        if (response.domain) setLastDeployDomain(response.domain);
        if (response.url) setDeployedUrl(response.url);
        openComposeWorkspace(appName, {
          ...buildWorkspaceInfo(appName, response, response, isCompose),
          isSSR: Boolean(isSSR || response.isSSR),
          autoDeploy: response.autoDeploy === true,
          initialTab: 'ai',
          isDeploying: false,
          internalPort: resultMeta.internalPort,
          cpus: resultMeta.cpus,
          memory: resultMeta.memory,
          image: resultMeta.image,
          deployStartedAt: deployStartedAtRef.current,
          onAiVerified: (payload) => {
            finishFromWorkspace({
              ...resultMeta,
              ...payload,
              result: { ...resultMeta, ...(payload.result || {}) }
            });
          }
        });
      }

      setRebuildLoading(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setDeployError(`Deployment error: ${err.message}`);
      setDeployDurationMs((prev) => prev != null ? prev : (Date.now() - (deployStartedAtRef.current || Date.now())));
      setRebuildLoading(false);
      openComposeWorkspace(appName, {
        isDeploying: false,
        deploymentType,
        basePath,
        autoDeploy: false,
        initialTab: 'ai',
        setupError: err.message || 'Automatic setup failed. Use Terminal, Files, or AI to install and run the app.',
        internalPort: port,
        cpus,
        memory,
        image,
        deployStartedAt: deployStartedAtRef.current,
        onAiVerified: (payload) => finishFromWorkspace(payload)
      });
    }
  };

  const parseRawEnv = (rawText) => {
    const vars = [];
    const lines = rawText.split('\n');
    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith('#')) return;
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx > 0) {
        const k = trimmed.substring(0, eqIdx).trim();
        const v = trimmed.substring(eqIdx + 1).trim();
        if (k) vars.push({ key: k, value: v });
      }
    });
    return vars;
  };

  const toggleEnvMode = () => {
    if (envMode === 'ui') {
      const rawText = repoEnvVars.map(ev => `${ev.key}=${ev.value}`).join('\n');
      setRepoRawEnv(rawText);
      setEnvMode('raw');
    } else {
      const vars = parseRawEnv(repoRawEnv);
      setRepoEnvVars(vars);
      setEnvMode('ui');
    }
  };

  const handleAddEnv = () => {
    if (!envKey.trim()) return;
    setRepoEnvVars([...repoEnvVars, { key: envKey.trim(), value: envValue }]);
    setEnvKey('');
    setEnvValue('');
  };

  const handleRemoveEnv = (idx) => {
    setRepoEnvVars(repoEnvVars.filter((_, i) => i !== idx));
  };

  const handleEnvFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const content = evt.target.result;
      if (envMode === 'raw') {
        setRepoRawEnv((prev) => prev ? prev + '\n' + content : content);
      } else {
        const vars = parseRawEnv(content);
        setRepoEnvVars(prev => [...prev, ...vars]);
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  };

  const handleAdditionalFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        setRepoAdditionalFiles(prev => {
          const filtered = prev.filter(f => f.name !== file.name);
          return [...filtered, { name: file.name, content: evt.target.result, type: file.type }];
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = null;
  };

  const handleRemoveAdditionalFile = (idx) => {
    setRepoAdditionalFiles(repoAdditionalFiles.filter((_, i) => i !== idx));
  };

  const pollDeploymentStatus = (deploymentId, opts = {}) => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Wait briefly before first poll so the backend can create the session,
    // then poll quickly so the workspace provisioning overlay stays live.
    const delay = 800;
    let attempts = 0;
    const maxAttempts = 225; // ~3 minutes at 800ms, then continue at same pace for long clones

    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      
      try {
        const session = await refreshDeploymentSession(deploymentId);
        
        if (!mountedRef.current) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return;
        }

        // If session doesn't exist yet (404), retry a few times before giving up
        if (!session) {
          if (attempts > 10) { // After 20 seconds (10 * 2s), show warning
            console.warn('[Deployment] Session not found after multiple attempts');
            if (attempts > maxAttempts) {
              setDeployError('Deployment session expired or not found.');
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              if (typeof openComposeWorkspace === 'function' && !opts.isStaticDeploy) {
                openComposeWorkspace(opts.appName || repoAppName, {
                  isDeploying: false,
                  deploymentType: opts.isCompose ? 'compose' : 'backend-workspace',
                  autoDeploy: false,
                  initialTab: 'ai',
                  setupError: 'Deployment session expired or timed out.',
                  deployStartedAt: deployStartedAtRef.current
                });
              }
            }
            return;
          }
          return; // Keep polling
        }

        if (session.status === 'failed' || session.status === 'cancelled') {
          const isCancelled = session.status === 'cancelled';
          const errObj = session.error && typeof session.error === 'object' ? session.error : { message: String(session.error || (isCancelled ? 'Deployment canceled by user.' : 'Deployment failed.')) };
          setDeployError(errObj.message || (isCancelled ? 'Deployment canceled by user.' : 'Deployment failed.'));
          setDeployErrorCode(errObj.code || null);
          setDeployErrorHint(errObj.hint || '');
          setDeployDurationMs((prev) => prev != null ? prev : (Date.now() - (deployStartedAtRef.current || Date.now())));
          if (isCancelled) {
            if (typeof closeComposeWorkspace === 'function') {
              closeComposeWorkspace({ cancelled: true });
            }
          } else if (typeof openComposeWorkspace === 'function' && !opts.isStaticDeploy) {
            openComposeWorkspace(opts.appName || repoAppName, {
              isDeploying: false,
              deploymentType: opts.isCompose ? 'compose' : 'backend-workspace',
              autoDeploy: false,
              initialTab: 'ai',
              setupError: errObj.message || 'Deployment failed.',
              deployStartedAt: deployStartedAtRef.current
            });
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setRebuildLoading(false);
          return;
        } else if (session.status === 'completed' || session.status === 'success' || session.status === 'done' || 
                   (session.milestones && session.milestones.length > 0 && 
                    ['completed', 'done', 'success'].includes(session.milestones[session.milestones.length - 1].status))) {
          setDeployStep(4);
          const finishedAt = Date.now();
          // Keep the timer running until Workspace AI verifies — only freeze if already set.
          const result = session.result || {};
          const url = session.url || result.url || '';
          const domain = result.domain || (url ? String(url).replace(/^https?:\/\//, '').replace(/\/$/, '') : '');
          setDeployedUrl(url || (domain ? `http://${domain}/` : ''));
          setLastDeployDomain(domain || '');
          const resultMeta = {
            ...result,
            url: url || (domain ? `http://${domain}/` : result.url),
            domain,
            cpus: result.cpus || opts.cpus || workspaceCpu,
            memory: result.memory || opts.memory || workspaceMemory,
            image: result.image || opts.image || backendSuggestedImage,
            internalPort: result.internalPort || opts.port || backendPort,
            containerName: result.containerName || result.runnerContainer || (result.ssh && result.ssh.containerId) || null,
            port: result.port || null
          };
          lastDeployResultRef.current = resultMeta;
          setLastDeployResult(resultMeta);
          if (opts.isCompose) setLastDeployKind('compose');
          else if (result.isSSR || opts.isSSR) setLastDeployKind('ssr');
          else if (opts.isStaticDeploy) setLastDeployKind('repo');
          else if (result.type === 'docker' || result.runnerContainer || result.ssh || result.port) setLastDeployKind('backend-workspace');

          // Static builds are done once the session completes — show the success page.
          if (opts.isStaticDeploy) {
            setDeployDurationMs(finishedAt - (deployStartedAtRef.current || finishedAt));
            setWorkspaceVerified(true);
            setRebuildLoading(false);
            setActiveTab('repo-deploying');
            if (onTabChange) onTabChange('repo-deploying');
            onStatusMessage('Deployment successful — details below', 'success');
          } else if (typeof openComposeWorkspace === 'function') {
            // Hand the live container to the workspace so AI can install/start/verify.
            const targetName = opts.appName || repoAppName;
            const isCompose = Boolean(opts.isCompose);
            openComposeWorkspace(targetName, {
              ...buildWorkspaceInfo(targetName, result, session, isCompose),
              isSSR: Boolean(result.isSSR || opts.isSSR),
              autoDeploy: result.autoDeploy === true,
              initialTab: 'ai',
              isDeploying: false,
              internalPort: resultMeta.internalPort,
              cpus: resultMeta.cpus,
              memory: resultMeta.memory,
              image: resultMeta.image,
              deployStartedAt: deployStartedAtRef.current,
              onAiVerified: (payload) => {
                const doneAt = Date.now();
                setDeployDurationMs(doneAt - (deployStartedAtRef.current || doneAt));
                const verification = payload && payload.verification;
                const merged = {
                  ...resultMeta,
                  ...(payload || {}),
                  ...(payload && payload.result ? payload.result : {}),
                  verification,
                  bindings: (payload && payload.bindings) || verification?.bindings || resultMeta.bindings || [],
                  apps: (payload && payload.apps) || verification?.apps || resultMeta.apps || [],
                  publicUrls: (payload && payload.publicUrls) || verification?.publicUrls || resultMeta.publicUrls || []
                };
                if (!merged.url && merged.domain) merged.url = `http://${merged.domain}/`;
                lastDeployResultRef.current = merged;
                setLastDeployResult(merged);
                if (merged.domain) setLastDeployDomain(merged.domain);
                if (merged.url) setDeployedUrl(merged.url);
                setWorkspaceVerified(true);
                setRebuildLoading(false);
                setActiveTab('repo-deploying');
                if (onTabChange) onTabChange('repo-deploying');
                onStatusMessage('Deployment successful — details below', 'success');
              }
            });
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
        }
      } catch (err) {
        console.error('Polling error', err);
        
        if (!mountedRef.current) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return;
        }

        if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('NetworkError'))) {
          console.warn('[Deployment] Network glitch during polling, will retry next tick');
          return; // Keep polling
        }

        // For other errors, stop polling and show error
        setDeployError(`Deployment monitoring error: ${err.message}`);
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    }, delay);
  };

  const handleCancelDeployment = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      if (deploymentSessionId) {
      await api.cancelDeployment(deploymentSessionId);
      }
      if (!mountedRef.current) return;
      setDeployError('Deployment canceled by user.');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setRebuildLoading(false);
      if (typeof closeComposeWorkspace === 'function') {
        closeComposeWorkspace({ cancelled: true });
      }
      if (onStatusMessage) {
        onStatusMessage('Deployment cancelled', 'info');
      }
    } catch (err) {
      console.error('Failed to cancel deployment:', err);
      if (!mountedRef.current) return;
      setDeployError('Deployment canceled by user.');
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setRebuildLoading(false);
      if (typeof closeComposeWorkspace === 'function') {
        closeComposeWorkspace({ cancelled: true });
      }
      if (onStatusMessage) {
        onStatusMessage('Deployment cancelled', 'info');
      }
    } finally {
      if (mountedRef.current) {
        setCancelLoading(false);
      }
    }
  };

  const handleScanEntryPoints = async () => {
    setIsScanningEntryPoints(true);
    setDeployError('');
    try {
      const branch = (repoBranch || fetchedRepoData?.branch || '').trim() || (gitProvider === 'gitlab' ? 'master' : 'main');
      const data = await api.scanGitEntries(fetchedRepoData?.repoUrl, branch);
      if (data && (data.entryPoints || data.apps)) {
        const detected = data.entryPoints || data.apps;
        const mappedEps = detected.map(app => ({
          path: app.path || app.directory || './',
          role: app.type || app.role || 'service',
          port: app.port || (app.type === 'frontend' ? 3000 : 8080),
          domain: app.domain || app.subdomain || ''
        }));
        
        const uniqueEps = [];
        const seenPorts = new Set();
        for (const ep of mappedEps) {
          if (ep.port && !seenPorts.has(ep.port)) {
            seenPorts.add(ep.port);
            uniqueEps.push(ep);
          }
        }
        setRepoEntryPoints(uniqueEps);
      }
    } catch (err) {
      setDeployError(err.message || 'Failed to scan entry points');
    } finally {
      setIsScanningEntryPoints(false);
    }
  };

  const handleConfigureAndDeploy = async (e) => {
    e.preventDefault();
    if (!repoAppName || !validateRepoAppName(repoAppName)) {
      onStatusMessage(repoAppNameError || 'Please provide a valid Project Name', 'error');
      return;
    }
    let finalEnvVars = repoEnvVars;
    if (envMode === 'raw') {
      finalEnvVars = parseRawEnv(repoRawEnv);
      setRepoEnvVars(finalEnvVars);
    }

    await startGitWorkspaceDeploy({
      appName: repoAppName,
      repoUrl: fetchedRepoData?.repoUrl,
      branch: (repoBranch || fetchedRepoData?.branch || '').trim() || (gitProvider === 'gitlab' ? 'master' : 'main'),
            rootDir: repoRootDirectory || './',
      isCompose: deployModeOverride === 'compose' || (deployModeOverride === 'auto' && composeDetected),
      isSSR: deployModeOverride === 'ssr' || (deployModeOverride === 'auto' && Boolean(ssrDetected || fetchedRepoData?.isSSR)),
      isStatic: deployModeOverride === 'auto'
        ? (!composeDetected && !backendDetected && !(ssrDetected || fetchedRepoData?.isSSR))
        : false, // compose | backend | ssr → never static
      composeEntry: composeEntryService,
      composePort: composeInternalPort,
      image: backendSuggestedImage || fetchedRepoData?.suggestedImage || 'alma-node',
              port: backendPort,
      exposedPortsStr: exposedPortsStr,
      cpus: workspaceCpu,
      memory: workspaceMemory,
      envVars: finalEnvVars,
      additionalFiles: repoAdditionalFiles,
      entryPoints: repoEntryPoints.length > 0 ? repoEntryPoints : undefined
    });
  };

  // CTA: static only in auto mode when nothing backend/SSR/compose was detected.
  const isStaticDeploy = deployModeOverride === 'auto'
    && !composeDetected
    && !backendDetected
    && !(ssrDetected || fetchedRepoData?.isSSR);

  return (
    <>
        {activeTab === 'repo' && (
          <div className="vercel-form-body">
            <button className="vercel-back-btn-new" onClick={() => { setRepoMode('connect'); setActiveTab(null); }}>
              <ArrowLeft size={16} /> <span>Back to deployment types</span>
            </button>

            <div>

              {/* Vercel-like Header */}
              <div style={{ marginBottom: '2.5rem', marginTop: '1rem' }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: 800, margin: '0 0 0.5rem 0', color: 'var(--text-main)', letterSpacing: '-0.03em' }}>Let's build something new.</h1>
                <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', margin: 0, fontWeight: 500 }}>To deploy a new Project, import an existing Git Repository.</p>
              </div>

              <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {/* Left side: Import Git Repository */}
                <div style={{ flex: '2 1 500px', border: '1px solid var(--border)', borderRadius: '0', overflow: 'hidden', background: 'var(--surface)', boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)' }}>
                  <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--surface-2)' }}>
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>Import Git Repository</h2>
                  </div>

                  {/* Provider Switcher (always shown) */}
                  <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)' }}>
                    {['github', 'gitlab'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => { setGitProvider(p); setReposFetchError(''); }}
                        style={{
                          flex: 1, padding: '0.7rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                          fontSize: '0.9rem', fontWeight: 600, textTransform: 'capitalize',
                          background: gitProvider === p ? 'var(--surface)' : 'transparent',
                          color: gitProvider === p ? 'var(--text-main)' : 'var(--text-muted)',
                          borderBottom: gitProvider === p ? '2px solid var(--primary)' : '2px solid transparent'
                        }}
                      >
                        {p === 'github' ? <Github size={16} /> : <Gitlab size={16} />} {p}
                      </button>
                    ))}
                  </div>

                  {/* OAuth or Repo Selection */}
                  {!gitProviders[gitProvider] ? (
                    <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                      {gitProvider === 'github' ? <Github size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '1.5rem' }} /> : <Gitlab size={48} style={{ color: '#e24329', opacity: 0.8, marginBottom: '1.5rem' }} />}
                      <h3 style={{ fontSize: '1.3rem', fontWeight: 800, marginBottom: '0.5rem', color: 'var(--text-main)' }}>Connect {gitProvider === 'github' ? 'GitHub' : 'GitLab'}</h3>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '2.5rem', fontSize: '1rem' }}>Connect your {gitProvider === 'github' ? 'GitHub' : 'GitLab'} account to import your repositories.</p>
                      
                      {connectError && (
                        <div style={{ background: 'rgba(var(--danger-rgb), 0.1)', color: 'var(--danger)', padding: '0.75rem', borderRadius: '0', marginBottom: '1.5rem', border: '1px solid rgba(var(--danger-rgb), 0.2)', fontSize: '0.9rem', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                          <AlertCircle size={16} style={{ flexShrink: 0, marginRight: '0.5rem' }} />
                          {connectError}
                        </div>
                      )}

                      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        {gitProvider === 'github' ? (
                          <button type="button" onClick={() => handleConnectGitProvider('github')} className="premium-deploy-btn" style={{ background: '#24292e', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem' }}>
                            <Github size={20} /> Continue with GitHub
                          </button>
                        ) : (
                          <button type="button" onClick={() => handleConnectGitProvider('gitlab')} className="premium-deploy-btn" style={{ background: '#e24329', color: 'white', border: 'none', padding: '0.75rem 1.5rem', fontSize: '1rem' }}>
                            <Gitlab size={20} /> Continue with GitLab
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Connected Account Bar */}
                      <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(var(--primary-rgb), 0.05)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <div style={{ width: '28px', height: '28px', background: 'var(--bg)', borderRadius: '0', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', color: 'var(--text-main)' }}>
                            {gitProvider === 'github' ? <Github size={16} /> : <Gitlab size={16} />}
                          </div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)', textTransform: 'capitalize' }}>{gitProvider} Account</div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {gitProvider === 'github' && (
                            <button type="button" onClick={handleManageGithubApp} title="Install the Devorants GitHub App on your repos to enable push-to-deploy" style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--primary)', padding: '0.4rem 0.8rem', borderRadius: '0', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s' }} className="vercel-btn-sm">
                              <GitBranch size={14} /> Auto-deploy
                            </button>
                          )}
                          <button type="button" onClick={handleDisconnectGitProvider} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', padding: '0.4rem 0.8rem', borderRadius: '0', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }} className="vercel-btn-sm">
                            Disconnect
                          </button>
                        </div>
                      </div>

                      {/* Search Bar */}
                      <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', display: 'flex', gap: '0.5rem', background: 'var(--surface-2)' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                          <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
                          <input type="text" placeholder="Search repositories..." value={gitSearch} onChange={(e) => setGitSearch(e.target.value)} style={{ width: '100%', padding: '0.85rem 1rem 0.85rem 2.75rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '0', fontSize: '1rem', color: 'var(--text-main)', transition: 'all 0.2s', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)', outline: 'none' }} onFocus={(e) => e.target.style.borderColor = 'var(--primary)'} onBlur={(e) => e.target.style.borderColor = 'var(--border)'} />
                        </div>
                      </div>

                      {/* List */}
                      <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        {gitReposLoading ? (
                          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-tertiary)' }}><LoaderCircle size={32} className="deploy-spin" /></div>
                        ) : reposFetchError ? (
                          <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: '0.5rem' }}>Failed to load repositories</div>
                            <div style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem', marginBottom: '1rem' }}>{reposFetchError}</div>
                            <button type="button" onClick={() => fetchGitRepos(gitProvider)} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '0.45rem 1.25rem', borderRadius: '0', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)', cursor: 'pointer' }}>
                              Retry
                            </button>
                          </div>
                        ) : gitRepos.length === 0 ? (
                          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-tertiary)' }}>No repositories found.</div>
                        ) : (
                          gitRepos.map((repo) => (
                            <div key={`${repo.provider}-${repo.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 1.5rem', borderBottom: '1px solid var(--border)', transition: 'background 0.2s', cursor: 'default' }} className="vercel-repo-row">
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '36px', height: '36px', background: 'var(--surface-2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', color: 'var(--text-main)' }}>
                                  {repo.provider === 'github' ? <Github size={20} /> : <Gitlab size={20} />}
                                </div>
                                <div>
                                  <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-main)' }}>{repo.fullName || repo.name}</div>
                                  <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.2rem' }}>
                                    {repo.private ? <Lock size={12} /> : <Globe size={12} />} {getRelativeTime(repo.updatedAt)}
                                  </div>
                                </div>
                              </div>
                              <button type="button" onClick={() => handleSelectRepo(repo)} disabled={detectingRepoId === repo.id} style={{ background: 'var(--bg)', border: '1px solid var(--border)', padding: '0.45rem 1.25rem', borderRadius: '0', fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-main)', cursor: detectingRepoId === repo.id ? 'not-allowed' : 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.5rem' }} className="vercel-import-btn">
                                {detectingRepoId === repo.id ? <><LoaderCircle size={14} className="deploy-spin" /> Detecting</> : 'Import'}
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right side: Import Third-Party Git Repository */}
                <div style={{ flex: '1 1 350px', border: '1px solid rgba(var(--primary-rgb), 0.2)', borderRadius: '0', overflow: 'hidden', background: 'linear-gradient(to bottom, var(--surface), var(--surface-2))', boxShadow: '0 8px 30px rgba(var(--primary-rgb), 0.08)' }}>
                  <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(var(--primary-rgb), 0.1)' }}>
                    <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: 0, color: 'var(--primary)' }}>Import Third-Party Git Repository &rarr;</h2>
                  </div>
                  <div style={{ padding: '1.5rem' }}>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: '1.5' }}>
                      Deploy public or private repositories from any Git provider by entering the clone URL.
                    </p>
                    <div style={{ position: 'relative', display: 'flex', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', borderRadius: '0' }}>
                      <div style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', display: 'flex', color: 'var(--text-tertiary)' }}><Globe size={18} /></div>
                      <input type="text" placeholder="https://github.com/provider/repo.git" value={repoUrl} onChange={(e) => setRepoUrl(e.target.value)} style={{ width: '100%', padding: '0.85rem 0.85rem 0.85rem 2.75rem', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '0', fontSize: '0.95rem', borderRight: 'none', color: 'var(--text-main)', outline: 'none', transition: 'border-color 0.2s' }} onFocus={(e) => e.target.style.borderColor = 'var(--primary)'} onBlur={(e) => e.target.style.borderColor = 'var(--border)'} />
                      <button type="button" onClick={async (e) => {
                        e.preventDefault();
                        if (!repoUrl) return;
                        const inferredName = repoUrl.split('/').pop().replace('.git', '');
                        const cleanAppName = (repoAppName || inferredName).replace(/[^a-zA-Z0-9-]/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'my-app';
                        setRepoAppName(cleanAppName);
                        setFetchedRepoData({ appName: cleanAppName, owner: user?.username || 'user', repoUrl, branch: 'main' });
                        await startGitWorkspaceDeploy({
                          appName: cleanAppName,
                          repoUrl,
                          branch: 'main',
                          rootDir: './',
                          isCompose: false,
                          image: backendSuggestedImage,
                          port: backendPort
                        });
                      }} style={{ background: 'linear-gradient(135deg, var(--primary), var(--primary-hover, #0056b3))', color: 'white', border: 'none', padding: '0 1.5rem', borderRadius: '0', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'opacity 0.2s' }} onMouseOver={(e) => e.target.style.opacity = '0.9'} onMouseOut={(e) => e.target.style.opacity = '1'}>Import</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'repo-configure' && !fetchedRepoData && (
          <div className="vercel-form-body git-configure-page">
            <div className="git-configure-hero">
              <h1>Restoring configuration…</h1>
              <p>If nothing appears, pick your repository again from the list.</p>
            </div>
            <button
              type="button"
              className="git-deploy-btn-vercel git-cfg-submit"
              onClick={() => {
                setActiveTab('repo');
                if (onTabChange) onTabChange('repo');
              }}
            >
              <ArrowLeft size={16} />
              <span>Back to repositories</span>
            </button>
          </div>
        )}

        {activeTab === 'repo-configure' && fetchedRepoData && (
          <div className="vercel-form-body git-configure-page">
            <button type="button" className="vercel-back-btn-new" onClick={() => setActiveTab('repo')}>
              <ArrowLeft size={16} /> <span>Back to selection</span>
            </button>

            <div className="git-configure-hero">
              <h1>Configure deployment</h1>
              <p>
                Review detected settings, set port and specs, then open the AI workspace.
              </p>
            </div>

            <div className="git-configure-layout">
              <aside className="git-project-card">
                <div className="git-project-card-top">
                  <div className="git-project-card-icon">
                    {repoFramework === 'nextjs' ? <Rocket size={18} /> : repoFramework === 'auto' ? <Wand2 size={18} /> : <Code size={18} />}
                  </div>
                  <div>
                    <div className="git-project-card-name">{repoAppName || 'Project Name'}</div>
                    <div className="git-project-card-sub">Repository import</div>
                  </div>
                </div>

                <div className="git-project-meta">
                  <div className="git-project-meta-item">
                    <div className="git-section-label">Repository</div>
                    <div className="git-project-meta-value">
                      <Github size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      {fetchedRepoData.repoUrl.split('/').slice(-2).join('/')}
                    </div>
                  </div>
                  <div className="git-project-meta-item">
                    <div className="git-section-label">Branch</div>
                    <div className="git-project-meta-value">
                      <GitBranch size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      {repoBranch || fetchedRepoData.branch || 'auto'}
                    </div>
                  </div>
                  <div className="git-project-meta-item">
                    <div className="git-section-label">Framework</div>
                    <div className="git-project-meta-value" style={{ textTransform: 'capitalize' }}>
                      {repoFramework === 'auto' ? <Wand2 size={14} style={{ color: 'var(--text-muted)' }} /> : <Code size={14} style={{ color: 'var(--text-muted)' }} />}
                      {repoFramework === 'auto' ? 'Auto-Detect' : repoFramework}
                    </div>
                  </div>
                  <div className="git-project-meta-item">
                    <div className="git-section-label">Deploy mode</div>
                    <div className="git-project-meta-value">
                      <select 
                        value={deployModeOverride} 
                        onChange={e => setDeployModeOverride(e.target.value)}
                        style={{ border: 'none', background: 'transparent', outline: 'none', color: 'var(--text-main)', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}
                      >
                        <option value="auto">Auto-Detect</option>
                        {composeDetected && <option value="compose">Compose</option>}
                        {(ssrDetected || fetchedRepoData?.isSSR) && <option value="ssr">SSR · PM2</option>}
                        {backendDetected && <option value="backend">Backend Workspace</option>}
                      </select>
                  </div>
                </div>
              </div>

                <div className="form-group" style={{ marginBottom: '1.5rem', marginTop: '0.5rem' }}>
                  <label>Ports to Open</label>
                  <div className="premium-input-wrapper">
                    <input 
                      type="text" 
                      value={exposedPortsStr} 
                      onChange={e => setExposedPortsStr(e.target.value)} 
                      placeholder="e.g. 3000, 3306" 
                    />
                  </div>
                  <p className="form-feedback" style={{ marginTop: '0.25rem' }}>Comma-separated list of ports to expose during container creation.</p>
                </div>

                {(ssrDetected || fetchedRepoData?.isSSR) && !composeDetected && (
                  <div className="git-ssr-callout">
                    <Server size={16} style={{ color: '#0284c7', flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <strong>SSR deployment</strong>
                      <p>
                        This app will build for production and run with PM2 in the workspace
                        (HOST=0.0.0.0, assigned PORT).
                      </p>
                    </div>
                  </div>
                )}
              </aside>

              <form className="git-configure-form" onSubmit={handleConfigureAndDeploy}>
                <div className="git-cfg-card">
                  <div className="form-group">
                    <label htmlFor="repoAppNameCfg">Project Name</label>
                    <div className="premium-input-wrapper">
                      <input
                        id="repoAppNameCfg"
                        type="text"
                        placeholder="my-app"
                        required
                        value={repoAppName}
                        onChange={handleRepoAppNameChange}
                        style={repoAppNameError ? { borderColor: 'var(--danger, #ef4444)' } : {}}
                      />
                    </div>
                    {repoAppNameError ? (
                      <p className="form-feedback" style={{ color: 'var(--danger, #ef4444)', marginTop: '0.25rem', fontWeight: 500 }}>
                        {repoAppNameError}
                      </p>
                    ) : (
                      <span className="git-form-hint">Lowercase letters, numbers, and hyphens only.</span>
                    )}
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="repoRootDirCfg">Root Directory</label>
                    <div className="premium-input-wrapper">
                      <input
                        id="repoRootDirCfg"
                        type="text"
                        placeholder="./"
                        value={repoRootDirectory}
                        onChange={e => setRepoRootDirectory(e.target.value)}
                      />
                    </div>
                    <span className="git-form-hint">Directory that contains your app root.</span>
                  </div>
                  </div>

                <div className="git-cfg-card">
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label htmlFor="repoBranchCfg">Git Branch</label>
                    <div className="premium-input-wrapper">
                      <input
                        id="repoBranchCfg"
                        type="text"
                        placeholder={gitProvider === 'gitlab' ? 'master' : 'main'}
                        value={repoBranch}
                        onChange={(e) => {
                          const next = e.target.value;
                          setRepoBranch(next);
                          setFetchedRepoData((prev) => (prev ? { ...prev, branch: next } : prev));
                        }}
                      />
                    </div>
                    <span className="git-form-hint">
                      Use the repository default branch. Many GitLab repos use <b>master</b>, not main.
                      If left wrong, deploy will try the remote default automatically.
                    </span>
                  </div>
                </div>

                {!composeDetected && composeInfo && (
                  <div className="git-cfg-card" style={{ background: 'rgba(237,72,83,0.06)', borderColor: 'rgba(237,72,83,0.3)' }}>
                    <div className="git-cfg-card-title">
                      <Package size={16} style={{ color: 'var(--danger)' }} />
                      Invalid Docker Compose file
                    </div>
                    <p className="git-cfg-card-desc" style={{ marginBottom: 0 }}>
                      Found <b>{composeInfo.composeFile}</b> but it is not a valid Docker Compose configuration
                      {composeInfo.reason ? ` — ${composeInfo.reason}` : ''}. Fix the file in the repository, then pick this repo again.
                    </p>
                  </div>
                )}

                {(deployModeOverride === 'compose' || (deployModeOverride === 'auto' && composeDetected)) && composeInfo && (
                  <div className="git-cfg-card" style={{ background: 'rgba(36,150,237,0.05)', borderColor: 'rgba(36,150,237,0.28)' }}>
                    <div className="git-cfg-card-title">
                      <Package size={16} style={{ color: 'var(--primary)' }} />
                      Docker Compose
                    </div>
                    <p className="git-cfg-card-desc">
                      Detected <b>{composeInfo.composeFile}</b>.
                      {Array.isArray(composeInfo.services) && composeInfo.services.length > 0 && (
                        <> Services: <b>{composeInfo.services.map(s => s.name).join(', ')}</b>.</>
                      )}
                    </p>
                    <div className="form-row-grid" style={{ gap: '0.9rem' }}>
                      <div className="form-group">
                        <label>Entry Service</label>
                        {Array.isArray(composeInfo.services) && composeInfo.services.length > 0 ? (
                          <div className="premium-input-wrapper">
                            <select value={composeEntryService} onChange={e => setComposeEntryService(e.target.value)}>
                              {composeInfo.services.map(s => (
                                <option key={s.name} value={s.name}>{s.name}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="premium-input-wrapper">
                            <input type="text" value={composeEntryService} onChange={e => setComposeEntryService(e.target.value)} />
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>Internal Port</label>
                        <div className="premium-input-wrapper">
                          <input type="number" value={composeInternalPort} onChange={e => setComposeInternalPort(Number(e.target.value) || 80)} />
                        </div>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
                      <label>Container Specs (vCPU & RAM)</label>
                      <div className="premium-input-wrapper">
                        <select
                          value={`${workspaceCpu}|${workspaceMemory}`}
                          onChange={(e) => {
                            const [cpu, memory] = e.target.value.split('|');
                            setWorkspaceCpu(cpu);
                            setWorkspaceMemory(memory);
                          }}
                        >
                          <option value="0.5|512m">Micro (0.5 vCPU / 512MB RAM)</option>
                          <option value="1.0|1024m">Basic (1 vCPU / 1GB RAM)</option>
                          <option value="2.0|2048m">Standard (2 vCPU / 2GB RAM)</option>
                          <option value="4.0|4096m">Pro (4 vCPU / 4GB RAM)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {(deployModeOverride === 'auto' ? (!composeDetected && !backendDetected && !(ssrDetected || fetchedRepoData?.isSSR)) : false) && (
                  <details className="git-cfg-details">
                    <summary className="premium-details-summary">
                      <Settings size={16} style={{ color: 'var(--text-muted)' }} />
                      <h3>Build and Output Settings</h3>
                      <span className="git-cfg-details-count">Optional</span>
                  </summary>
                    <div className="form-group" style={{ paddingBottom: '1rem' }}>
                      <p className="git-cfg-card-desc">Leave blank to use framework defaults.</p>
                      <div className="form-row-grid" style={{ marginBottom: '0.85rem', gap: '0.9rem' }}>
                      <div className="form-group">
                          <label>Build Command</label>
                        <div className="premium-input-wrapper">
                            <input type="text" placeholder="Auto-detected" value={repoBuildCommand} onChange={e => setRepoBuildCommand(e.target.value)} />
                        </div>
                      </div>
                      <div className="form-group">
                          <label>Output Directory</label>
                        <div className="premium-input-wrapper">
                            <input type="text" placeholder="Auto-detected" value={repoBuildOutputDir} onChange={e => setRepoBuildOutputDir(e.target.value)} />
                        </div>
                      </div>
                    </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label>Install Command</label>
                      <div className="premium-input-wrapper">
                          <input type="text" placeholder="Auto-detected" value={repoInstallCommand} onChange={e => setRepoInstallCommand(e.target.value)} />
                      </div>
                    </div>
                  </div>
                </details>
                )}

                {!composeDetected && !isStaticDeploy && (
                  <div className="git-cfg-card" style={{ marginBottom: '1.5rem' }}>
                    <div className="git-cfg-card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <Server size={16} style={{ color: 'var(--primary)' }} />
                        Entry Points (Microservices / Multi-app)
                      </div>
                      <button type="button" className="premium-ghost-btn" onClick={handleScanEntryPoints} disabled={isScanningEntryPoints}>
                        {isScanningEntryPoints ? <LoaderCircle size={14} className="spin" /> : <Search size={14} />} 
                        {isScanningEntryPoints ? 'Scanning...' : 'Scan Project'}
                      </button>
                    </div>
                    <p className="git-cfg-card-desc">
                      If this repository contains multiple applications (e.g. frontend and backend), scan it to auto-detect them and assign subdomains/ports.
                    </p>
                    {deployError && (
                      <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '0.5rem', padding: '0.5rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0' }}>
                        {deployError}
                      </div>
                    )}
                    
                    {repoEntryPoints.length > 0 && (
                      <div style={{ marginTop: '1rem', border: '1px solid var(--border)', borderRadius: '0', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.9rem' }}>
                          <thead style={{ background: 'var(--surface-2)' }}>
                            <tr>
                              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>Directory</th>
                              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>Role</th>
                              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>Port</th>
                              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>Subdomain</th>
                              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)', width: '40px' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {repoEntryPoints.map((ep, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                  <input 
                                    type="text" 
                                    value={ep.path || ''} 
                                    onChange={(e) => {
                                      const newEps = [...repoEntryPoints];
                                      newEps[idx].path = e.target.value;
                                      setRepoEntryPoints(newEps);
                                    }}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)' }}
                                  />
                                </td>
                                <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                  <input 
                                    type="text" 
                                    value={ep.role || ''} 
                                    onChange={(e) => {
                                      const newEps = [...repoEntryPoints];
                                      newEps[idx].role = e.target.value;
                                      setRepoEntryPoints(newEps);
                                    }}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)' }}
                                  />
                                </td>
                                <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                  <input 
                                    type="number" 
                                    value={ep.port || ''} 
                                    onChange={(e) => {
                                      const newEps = [...repoEntryPoints];
                                      newEps[idx].port = Number(e.target.value);
                                      setRepoEntryPoints(newEps);
                                    }}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)' }}
                                  />
                                </td>
                                <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                  <input 
                                    type="text" 
                                    value={ep.domain || ''} 
                                    placeholder="Auto-assigned"
                                    onChange={(e) => {
                                      const newEps = [...repoEntryPoints];
                                      newEps[idx].domain = e.target.value;
                                      setRepoEntryPoints(newEps);
                                    }}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-main)' }}
                                  />
                                </td>
                                <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border)' }}>
                                  <button type="button" onClick={() => setRepoEntryPoints(repoEntryPoints.filter((_, i) => i !== idx))} style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>
                                    <X size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div style={{ padding: '0.5rem', background: 'var(--surface-2)', borderRadius: '0', borderTop: repoEntryPoints.length > 0 ? 'none' : '1px solid var(--border)' }}>
                      <button type="button" className="premium-ghost-btn" style={{ width: '100%', justifyContent: 'center' }} onClick={() => setRepoEntryPoints([...repoEntryPoints, { path: './', role: 'service', port: 3000, domain: '' }])}>
                        + Add Entry Point manually
                      </button>
                    </div>
                  </div>
                )}

                {!composeDetected && !isStaticDeploy && (
                  <div className="git-cfg-card">
                    <div className="git-cfg-card-title">
                      <Settings size={16} style={{ color: 'var(--primary)' }} />
                      Workspace Configuration
                      {(ssrDetected || fetchedRepoData?.isSSR) && (
                        <span className="git-mode-badge ssr" style={{ marginLeft: '0.25rem' }}>SSR</span>
                      )}
                    </div>
                    <p className="git-cfg-card-desc">
                      {(ssrDetected || fetchedRepoData?.isSSR)
                        ? 'SSR apps install, build, and start with PM2 inside the workspace.'
                        : backendDetected
                          ? 'Backend workspace path — set port and specs before opening.'
                          : 'Values are applied to the workspace container before AI starts.'}
                    </p>
                    <div className="form-row-grid" style={{ gap: '0.9rem' }}>
                      <div className="form-group">
                        <label>Internal Port <span className="required-star">*</span></label>
                        <div className="premium-input-wrapper">
                          <input
                            type="number"
                            placeholder="3000"
                            min={1}
                            max={65535}
                            required
                            value={backendPort}
                            onChange={e => setBackendPort(Number(e.target.value) || 3000)}
                          />
                        </div>
                        <span className="git-form-hint">App listen port inside the container.</span>
                      </div>
                      <div className="form-group">
                        <label>Runtime Image</label>
                        <div className="premium-input-wrapper">
                          <select
                            value={backendSuggestedImage}
                            onChange={e => setBackendSuggestedImage(e.target.value)}
                          >
                            <option value="alma-node">Node.js workspace</option>
                            <option value="alma-python">Python workspace</option>
                            <option value="alma-linux">Alma Linux workspace</option>
                            <option value="node-mongo">Node + Mongo</option>
                            <option value="node-mysql">Node + MySQL</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="form-group" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
                      <label>Container Specs (vCPU & RAM) <span className="required-star">*</span></label>
                      <div className="premium-input-wrapper">
                        <select
                          value={`${workspaceCpu}|${workspaceMemory}`}
                          onChange={(e) => {
                            const [cpu, memory] = e.target.value.split('|');
                            setWorkspaceCpu(cpu);
                            setWorkspaceMemory(memory);
                          }}
                        >
                          <option value="0.5|512m">Micro (0.5 vCPU / 512MB RAM)</option>
                          <option value="1.0|1024m">Basic (1 vCPU / 1GB RAM)</option>
                          <option value="2.0|2048m">Standard (2 vCPU / 2GB RAM)</option>
                          <option value="4.0|4096m">Pro (4 vCPU / 4GB RAM)</option>
                        </select>
                  </div>
                      <span className="git-form-hint">Docker CPU and memory limits for this container.</span>
                    </div>
                  </div>
                )}

                <details open className="git-cfg-details">
                  <summary className="premium-details-summary">
                    <Lock size={16} style={{ color: 'var(--text-muted)' }} />
                    <h3>Environment Variables</h3>
                    <span className="git-cfg-details-count">{repoEnvVars.length} configured</span>
                  </summary>
                  <div className="form-group" style={{ paddingBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.65rem' }}>
                      <p className="git-cfg-card-desc" style={{ margin: 0 }}>Injected into the workspace container and .env.</p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <label className="premium-ghost-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', margin: 0 }}>
                            <input type="file" onChange={handleEnvFileUpload} style={{ display: 'none' }} accept=".env" />
                          <FileUp size={14} /> <span>Import</span>
                          </label>
                        <button type="button" onClick={toggleEnvMode} className="premium-ghost-btn">
                          {envMode === 'ui' ? 'Edit Raw' : 'UI mode'}
                          </button>
                        </div>
                      </div>

                      {envMode === 'ui' ? (
                        <>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', alignItems: 'center' }}>
                            <div className="premium-input-wrapper" style={{ flex: 1 }}>
                            <input type="text" placeholder="NAME" value={envKey} onChange={e => setEnvKey(e.target.value)} style={{ fontFamily: 'monospace' }} />
                            </div>
                            <div className="premium-input-wrapper" style={{ flex: 2 }}>
                            <input type="text" placeholder="VALUE" value={envValue} onChange={e => setEnvValue(e.target.value)} style={{ fontFamily: 'monospace' }} />
                            </div>
                          <button
                            type="button"
                            onClick={handleAddEnv}
                            style={{
                              padding: '0 0.9rem',
                              height: '38px',
                              background: 'var(--text-main)',
                              color: 'var(--bg)',
                              border: 'none',
                              borderRadius: '0',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              cursor: 'pointer'
                            }}
                          >
                            Add
                          </button>
                          </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                            {repoEnvVars.map((ev, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                background: 'var(--bg)',
                                padding: '0.5rem 0.75rem',
                                borderRadius: '0',
                                border: '1px solid var(--border-subtle)'
                              }}
                            >
                              <div style={{ flex: '0 0 30%', fontWeight: 700, color: 'var(--text-main)', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{ev.key}</div>
                              <div style={{ flex: '1', color: 'var(--text-muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8rem' }}>{ev.value}</div>
                              <button type="button" onClick={() => handleRemoveEnv(idx)} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '0.2rem', display: 'flex' }}>
                                <X size={16} />
                                </button>
                              </div>
                            ))}
                            {repoEnvVars.length === 0 && (
                            <div className="git-cfg-env-empty">No environment variables yet.</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="premium-input-wrapper">
                          <textarea
                            placeholder="KEY=VALUE&#10;PORT=3000"
                            value={repoRawEnv}
                            onChange={e => setRepoRawEnv(e.target.value)}
                            style={{
                              width: '100%',
                            minHeight: '160px',
                              background: '#0a0a0a',
                              color: '#e5e5e5',
                              border: '1px solid var(--border)',
                              borderRadius: '0',
                            padding: '0.85rem',
                              fontFamily: '"Fira Code", monospace',
                            fontSize: '0.85rem',
                              resize: 'vertical',
                              outline: 'none',
                            lineHeight: '1.45'
                            }}
                          />
                        </div>
                      )}
                  </div>
                </details>

                <details open className="git-cfg-details">
                  <summary className="premium-details-summary">
                    <FileUp size={16} style={{ color: 'var(--text-muted)' }} />
                    <h3>Additional Files</h3>
                    <span className="git-cfg-details-count">{repoAdditionalFiles.length} configured</span>
                  </summary>
                  <div className="form-group" style={{ paddingBottom: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem', flexWrap: 'wrap', gap: '0.65rem' }}>
                      <p className="git-cfg-card-desc" style={{ margin: 0 }}>Upload files required for deployment (e.g., config files, certs).</p>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <label className="premium-ghost-btn" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', margin: 0 }}>
                          <input type="file" multiple onChange={handleAdditionalFileUpload} style={{ display: 'none' }} />
                          <FileUp size={14} /> <span>Upload Files</span>
                        </label>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                      {repoAdditionalFiles.map((file, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg)', padding: '0.5rem 0.75rem', borderRadius: '0', border: '1px solid var(--border-subtle)' }}>
                          <div style={{ flex: '1', fontWeight: 700, color: 'var(--text-main)', fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>{file.name}</div>
                          <button type="button" onClick={() => handleRemoveAdditionalFile(idx)} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '0.2rem', display: 'flex' }}>
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      {repoAdditionalFiles.length === 0 && (
                        <div className="git-cfg-env-empty">No additional files uploaded.</div>
                      )}
                    </div>
                  </div>
                </details>

                <div className="git-cfg-actions">
                  {rebuildLoading && workspaceMinimized ? (
                    <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                      <button type="button" onClick={restoreWorkspace} className="git-deploy-btn-vercel git-cfg-submit" style={{ flex: 1 }}>
                        <Terminal size={16} />
                        <span>Open Workspace</span>
                      </button>
                      <button type="button" onClick={handleCancelDeployment} className="git-deploy-btn-vercel git-cfg-submit" style={{ flex: 1, background: 'var(--error-surface)', color: 'var(--error)', border: '1px solid var(--error-border)' }}>
                        <X size={16} />
                        <span>Cancel Deployment</span>
                  </button>
                    </div>
                  ) : rebuildLoading ? (
                    <div style={{ display: 'flex', gap: '1rem', width: '100%' }}>
                      <button type="button" disabled className="git-deploy-btn-vercel git-cfg-submit" style={{ flex: 1 }}>
                        <LoaderCircle size={16} className="deploy-spin" />
                        <span>{composeDetected || isStaticDeploy ? 'Deploying…' : 'Opening…'}</span>
                      </button>
                      <button type="button" onClick={handleCancelDeployment} className="git-deploy-btn-vercel git-cfg-submit" style={{ flex: 1, background: 'var(--error-surface)', color: 'var(--error)', border: '1px solid var(--error-border)' }}>
                        <X size={16} />
                        <span>Cancel Deployment</span>
                      </button>
                    </div>
                  ) : (
                    <button type="submit" disabled={Boolean(repoAppNameError) || !repoAppName.trim()} className="git-deploy-btn-vercel git-cfg-submit" style={repoAppNameError ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                      <Rocket size={16} />
                      <span>
                        {composeDetected ? 'Deploy Compose Stack' : (isStaticDeploy ? 'Deploy Static Build' : 'Open AI Workspace')}
                      </span>
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'repo-deploying' && (
          <div className="vercel-form-body">
            {(workspaceVerified && !deployError) ? (() => {
              const labelService = (entry = {}) => {
                if (entry.label) return entry.label;
                const pathHint = entry.path || entry.dir || entry.workDir || entry.role || '';
                const leaf = String(pathHint).split('/').filter(Boolean).pop() || '';
                const role = String(entry.role || '').toLowerCase();
                if (role === 'admin' || role === 'admin-dashboard' || role === 'admin_dashboard' || /admin/i.test(leaf)) return 'Admin Dashboard';
                if (role === 'user' || role === 'user-dashboard' || role === 'user_dashboard' || /user/i.test(leaf)) return 'User Dashboard';
                if (role === 'backend' || role === 'api' || role === 'server' || /backend|server|api/i.test(leaf)) return 'Backend API';
                if (role === 'frontend' || /front|client|web/i.test(leaf)) return 'Frontend';
                if (entry.primary) return 'Primary Application';
                if (leaf) return leaf.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                return 'Application';
              };

              const toLink = (entry = {}) => {
                const domain = entry.domain || (entry.url ? String(entry.url).replace(/^https?:\/\//, '').replace(/\/$/, '') : '');
                if (!domain && !entry.url) return null;
                const url = entry.url || (domain ? `http://${domain}/` : null);
                return {
                  url,
                  domain: domain || url,
                  port: entry.port || entry.internalPort || null,
                  role: entry.role || null,
                  path: entry.path || entry.dir || entry.workDir || null,
                  label: labelService(entry),
                  primary: Boolean(entry.primary || entry.kind === 'primary'),
                  fromAgent: Boolean(entry.fromAgent)
                };
              };

              // AI workspace / agent verification is the source of truth for Admin / User / Backend.
              const agentBindings = [
                ...(Array.isArray(lastDeployResult?.verification?.bindings) ? lastDeployResult.verification.bindings : []),
                ...(Array.isArray(lastDeployResult?.bindings) ? lastDeployResult.bindings : [])
              ].map((entry) => ({ ...entry, fromAgent: true }));
              const agentApps = [
                ...(Array.isArray(lastDeployResult?.verification?.apps) ? lastDeployResult.verification.apps : []),
                ...(Array.isArray(lastDeployResult?.apps) ? lastDeployResult.apps : [])
              ].map((entry) => ({ ...entry, fromAgent: true }));

              const pathKey = (entry = {}) => String(entry.path || entry.dir || entry.workDir || '').replace(/\/+$/, '').toLowerCase();
              const enrichFromBindings = (app) => {
                if (app.domain || app.url) return app;
                const appPath = pathKey(app);
                const appPort = app.port != null ? Number(app.port) : null;
                const appRole = String(app.role || '').toLowerCase();
                const match = agentBindings.find((b) => {
                  const bPath = pathKey(b);
                  const bPort = b.port != null ? Number(b.port) : null;
                  const bRole = String(b.role || '').toLowerCase();
                  if (appPort != null && bPort != null && appPort === bPort) return true;
                  if (appPath && bPath && (appPath === bPath || appPath.endsWith(bPath) || bPath.endsWith(appPath))) return true;
                  if (appRole && bRole && appRole === bRole) return true;
                  return false;
                });
                if (!match) return app;
                return {
                  ...app,
                  domain: match.domain || app.domain,
                  url: match.url || app.url,
                  port: app.port != null ? app.port : match.port,
                  role: app.role || match.role,
                  label: app.label || match.label,
                  primary: Boolean(app.primary || match.primary || match.kind === 'primary'),
                  fromAgent: true
                };
              };

              const rawSources = [
                ...agentApps.map(enrichFromBindings),
                ...agentBindings,
                ...(Array.isArray(lastDeployResult?.entryPoints) ? lastDeployResult.entryPoints : []),
                ...(Array.isArray(repoEntryPoints) ? repoEntryPoints : [])
              ];
              let deployLinks = rawSources.map(toLink).filter(Boolean);

              if (!deployLinks.length && Array.isArray(lastDeployResult?.publicUrls)) {
                deployLinks = lastDeployResult.publicUrls.map((url) => {
                  try {
                    const u = new URL(url);
                    return toLink({ url, domain: u.hostname, port: lastDeployResult.port });
                  } catch {
                    return toLink({ url, domain: url, port: lastDeployResult.port });
                  }
                }).filter(Boolean);
              }

              if (!deployLinks.length && (deployedUrl || lastDeployResult?.url || lastDeployDomain || lastDeployResult?.domain)) {
                deployLinks = [toLink({
                  url: deployedUrl || lastDeployResult?.url,
                  domain: lastDeployDomain || lastDeployResult?.domain,
                  port: lastDeployResult?.port || lastDeployResult?.internalPort || backendPort,
                  primary: true,
                  role: 'frontend'
                })].filter(Boolean);
              }

              const richness = (link) => {
                const label = String(link.label || '').toLowerCase();
                let score = 0;
                if (link.fromAgent) score += 8;
                if (link.role) score += 4;
                if (/admin dashboard|user dashboard|backend api|frontend|primary/.test(label)) score += 3;
                else if (link.label) score += 1;
                if (link.primary) score += 1;
                if (link.port != null) score += 1;
                if (link.path) score += 1;
                return score;
              };

              // Prefer richer agent-labeled entries when domains collide.
              const byDomain = new Map();
              for (const link of deployLinks) {
                const key = String(link.domain || link.url || '').toLowerCase();
                if (!key) continue;
                const prev = byDomain.get(key);
                if (!prev || richness(link) > richness(prev)) {
                  byDomain.set(key, link);
                }
              }
              deployLinks = [...byDomain.values()];

              const roleRank = (link) => {
                const role = String(link.role || '').toLowerCase();
                const label = String(link.label || '').toLowerCase();
                if (link.primary || role === 'frontend' || label.includes('frontend') || label.includes('primary')) return 1;
                if (role === 'admin' || role === 'admin-dashboard' || label.includes('admin')) return 2;
                if (role === 'user' || role === 'user-dashboard' || label.includes('user')) return 3;
                if (role === 'backend' || role === 'api' || role === 'server' || label.includes('backend')) return 4;
                return 5;
              };
              deployLinks.sort((a, b) => roleRank(a) - roleRank(b));

              const isLocalEnv = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
              deployLinks = deployLinks.map((link) => {
                let linkHref = link.url;
                if (isLocalEnv && link.port && typeof linkHref === 'string' && linkHref.includes('.devorants.com')) {
                  linkHref = `http://${window.location.hostname}:${link.port}/`;
                }
                return { ...link, linkHref };
              });

              const repoLabel = parseRepoFullName(fetchedRepoData?.repoUrl) || fetchedRepoData?.name || null;
              const branchLabel = repoBranch || fetchedRepoData?.branch || null;
              const deployTypeLabel = lastDeployKind === 'compose'
                ? 'Docker Compose'
                : lastDeployKind === 'ssr'
                  ? 'SSR (PM2)'
                  : lastDeployKind === 'backend-workspace'
                    ? 'AI Workspace'
                    : 'Static Frontend';
              const serviceTone = (link) => {
                const role = String(link.role || '').toLowerCase();
                const label = String(link.label || '').toLowerCase();
                if (role === 'admin' || label.includes('admin')) return 'admin';
                if (role === 'user' || label.includes('user')) return 'user';
                if (role === 'backend' || role === 'api' || role === 'server' || label.includes('backend')) return 'backend';
                if (link.primary || role === 'frontend' || label.includes('frontend') || label.includes('primary')) return 'frontend';
                return 'default';
              };
              const serviceIcon = (tone) => {
                if (tone === 'backend') return <Server size={18} />;
                if (tone === 'admin' || tone === 'user') return <Globe size={18} />;
                return <Globe size={18} />;
              };

              return (
              <div className="git-success-screen">
                  <div className="git-success-hero">
                    <div className="git-success-badge-wrap" aria-hidden="true">
                      <span className="git-success-badge-glow" />
                      <div className="git-success-badge">
                        <CheckCircle2 size={30} strokeWidth={2.4} />
                      </div>
                    </div>
                    <div className="git-success-status-pill">
                      <span className="git-success-status-dot" />
                      Verified & Live
                    </div>
                    <h1 className="git-success-title">Deployment Successful</h1>
                    <p className="git-success-sub">
                      <strong>{repoAppName}</strong>
                      <span>is live in {user?.username || 'your'} projects</span>
                    </p>
                  </div>

                  {deployLinks.length > 0 && (
                    <section className="git-success-panel">
                      <div className="git-success-panel-head">
                        <div>
                          <h2 className="git-success-panel-title">Applications & Dashboards</h2>
                          <p className="git-success-panel-desc">Public endpoints from the AI workspace</p>
                        </div>
                        <span className="git-success-panel-count">{deployLinks.length}</span>
                      </div>
                      <div className="git-success-service-list">
                        {deployLinks.map((link, i) => {
                          const tone = serviceTone(link);
                          return (
                            <div key={`${link.domain}-${i}`} className={`git-success-service-card tone-${tone}`}>
                              <div className="git-success-service-icon">{serviceIcon(tone)}</div>
                              <div className="git-success-service-body">
                                <div className="git-success-service-title">
                                  <span>{link.label || 'Application'}</span>
                                  {link.primary && <span className="git-success-service-badge">Primary</span>}
                                  {link.port != null && <span className="git-success-service-port">:{link.port}</span>}
                                </div>
                                <div className="git-success-service-domain">{link.domain}</div>
                              </div>
                              {link.linkHref && (
                                <a
                                  href={link.linkHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="git-success-service-open"
                                >
                                  Open
                                  <ExternalLink size={14} />
                                </a>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <section className="git-success-panel">
                    <div className="git-success-panel-head">
                      <div>
                        <h2 className="git-success-panel-title">Deployment Details</h2>
                        <p className="git-success-panel-desc">
                          {lastDeployKind === 'compose'
                            ? 'Compose stack provisioned and verified.'
                            : lastDeployKind === 'ssr'
                              ? 'SSR application is running under PM2.'
                              : lastDeployKind === 'backend-workspace'
                                ? 'Workspace verified on its public URL.'
                                : 'Static build published successfully.'}
                        </p>
                      </div>
                      <div className="git-success-panel-icons">
                        <Package size={16} />
                        {gitProvider === 'github' ? <Github size={16} /> : <GitBranch size={16} />}
                      </div>
                    </div>
                    <div className="git-success-meta-grid">
                      <div className="git-success-meta-cell">
                        <div className="git-success-meta-label">Type</div>
                        <div className="git-success-meta-value">{deployTypeLabel}</div>
                      </div>
                      <div className="git-success-meta-cell">
                        <div className="git-success-meta-label">Status</div>
                        <div className="git-success-meta-value is-success">Verified</div>
                      </div>
                      <div className="git-success-meta-cell">
                        <div className="git-success-meta-label"><Clock size={11} /> Deploy Time</div>
                        <div className="git-success-meta-value is-mono">
                          {formatDeployDuration(deployDurationMs != null ? deployDurationMs : deployElapsedMs)}
                        </div>
                      </div>
                      {lastDeployResult?.port != null && (
                        <div className="git-success-meta-cell">
                          <div className="git-success-meta-label">Host Port</div>
                          <div className="git-success-meta-value is-mono">{lastDeployResult.port}</div>
                        </div>
                      )}
                      {(lastDeployResult?.internalPort || backendPort) && (
                        <div className="git-success-meta-cell">
                          <div className="git-success-meta-label">Internal Port</div>
                          <div className="git-success-meta-value is-mono">{lastDeployResult?.internalPort || backendPort}</div>
                        </div>
                      )}
                      <div className="git-success-meta-cell">
                        <div className="git-success-meta-label"><Cpu size={11} /> vCPU</div>
                        <div className="git-success-meta-value">{lastDeployResult?.cpus || workspaceCpu}</div>
                      </div>
                      <div className="git-success-meta-cell">
                        <div className="git-success-meta-label"><HardDrive size={11} /> RAM</div>
                        <div className="git-success-meta-value">{lastDeployResult?.memory || workspaceMemory}</div>
                      </div>
                      {(lastDeployResult?.image || backendSuggestedImage) && (
                        <div className="git-success-meta-cell">
                          <div className="git-success-meta-label">Runtime Image</div>
                          <div className="git-success-meta-value is-truncate" title={lastDeployResult?.image || backendSuggestedImage}>
                            {lastDeployResult?.image || backendSuggestedImage}
                          </div>
                        </div>
                      )}
                      {(repoLabel || branchLabel) && (
                        <div className="git-success-meta-cell git-success-meta-span">
                          <div className="git-success-meta-label"><Github size={11} /> Repository</div>
                          <div className="git-success-meta-value">
                            {repoLabel || 'Repository'}
                            {branchLabel ? <span className="git-success-meta-muted"> · {branchLabel}</span> : null}
                          </div>
                        </div>
                      )}
                      {lastDeployResult?.containerName && (
                        <div className="git-success-meta-cell git-success-meta-span">
                          <div className="git-success-meta-label"><Box size={11} /> Container</div>
                          <div className="git-success-meta-value is-mono is-truncate" title={lastDeployResult.containerName}>
                            {lastDeployResult.containerName}
                          </div>
                        </div>
                      )}
                    </div>
                  </section>

                  {gitProvider === 'github' && (
                    <section className="git-success-panel git-success-panel--compact">
                      <div className="git-success-cd-row">
                        <div className="git-success-cd-icon"><GitBranch size={16} /></div>
                        <div>
                          <div className="git-success-panel-title">Continuous Deployment</div>
                          <p className="git-success-panel-desc">
                            Auto-deploy linked to <strong>{parseRepoFullName(fetchedRepoData?.repoUrl) || 'this repository'}</strong>
                            {' '}on branch <strong>{fetchedRepoData?.branch || 'main'}</strong>
                          </p>
                        </div>
                      </div>
                    </section>
                  )}

                  <div className="git-success-actions">
                    <button
                      type="button"
                      className="git-success-btn git-success-btn--primary"
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('switch-to-dashboard'));
                        if (onTabChange) onTabChange('deployments');
                      }}
                    >
                      Continue to Dashboard
                    </button>
                    {typeof openComposeWorkspace === 'function' && (
                      <button
                        type="button"
                        className="git-success-btn git-success-btn--secondary"
                        onClick={() => {
                          const name = repoAppName || fetchedRepoData?.appName;
                          const isCompose = lastDeployKind === 'compose';
                          openComposeWorkspace(name, {
                            ...buildWorkspaceInfo(name, lastDeployResult || {}, null, isCompose),
                            isSSR: lastDeployKind === 'ssr' || Boolean(lastDeployResult?.isSSR || ssrDetected),
                            autoDeploy: false,
                            isDeploying: false,
                            internalPort: lastDeployResult?.internalPort || backendPort,
                            cpus: lastDeployResult?.cpus || workspaceCpu,
                            memory: lastDeployResult?.memory || workspaceMemory,
                            image: lastDeployResult?.image || backendSuggestedImage,
                            domain: lastDeployDomain || lastDeployResult?.domain || null,
                            url: deployedUrl || lastDeployResult?.url || null
                          });
                        }}
                      >
                        <Terminal size={16} />
                        Reopen Workspace
                      </button>
                    )}
                  </div>
              </div>
              );
            })() : (
              <div className="premium-progress-container">
              <div className={`premium-progress-header ${deployError ? 'failed' : ''}`}>
                <div className={`premium-progress-ring ${deployError ? 'failed' : ''}`}>
                  {deployError ? (
                    <AlertCircle size={24} style={{ color: 'var(--danger)' }} />
                  ) : (
                    <LoaderCircle size={24} className="deploy-spin" style={{ color: '#10b981' }} />
                  )}
                </div>
                
                <div className="premium-progress-title">
                  <strong style={{ color: deployError ? 'var(--danger)' : 'var(--text-main)' }}>
                    {deployError
                      ? (deployError.toLowerCase().includes('cancel') ? 'Deployment Cancelled' : 'Deployment Failed')
                      : `Deploying ${fetchedRepoData?.appName || 'Application'}`}
                  </strong>
                  <span>
                    {deployError
                      ? (deployError.toLowerCase().includes('cancel') ? 'Deployment was cancelled by user' : 'Deployment encountered an error')
                      : (lastDeployKind === 'repo'
                        ? 'Building and optimizing static assets...'
                        : 'Workspace is open — AI will install, start, and verify the app')}
                  </span>
                  <div style={{ marginTop: '0.55rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: deployError ? 'var(--text-secondary)' : 'var(--text-main)', fontWeight: 700, fontSize: '0.9rem', fontVariantNumeric: 'tabular-nums' }}>
                    <Clock size={15} style={{ color: 'var(--text-tertiary)' }} />
                    {deployError || deployDurationMs != null
                      ? `Took ${formatDeployDuration(deployDurationMs != null ? deployDurationMs : deployElapsedMs)}`
                      : `Elapsed ${formatDeployDuration(deployElapsedMs)}`}
                  </div>
                  {deployError && (
                    <div style={{ marginTop: '0.75rem', fontSize: '0.9rem', color: 'var(--text-secondary)', maxWidth: '640px', lineHeight: 1.5 }}>
                      <div style={{ color: 'var(--danger)', fontWeight: 600, marginBottom: '0.25rem' }}>{deployError}</div>
                      {deployErrorHint && <div>{deployErrorHint}</div>}
                    </div>
                  )}
                </div>

                {deployError ? (
                  <div className="premium-progress-cancel">
                    <button type="button" className="premium-cancel-btn" onClick={() => {
                      setDeployError('');
                      setActiveTab('repo-configure');
                      if (onTabChange) onTabChange('repo-configure');
                    }}>
                      <ArrowLeft size={16} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />
                      Back to Configure
                    </button>
                  </div>
                ) : (
                  <div className="premium-progress-cancel">
                    <button
                      type="button"
                      className="premium-cancel-btn"
                      disabled={cancelLoading}
                      onClick={handleCancelDeployment}
                    >
                      {cancelLoading ? (
                        <>
                          <LoaderCircle size={15} className="deploy-spin" />
                          Cancelling...
                        </>
                      ) : (
                        <>
                          <XCircle size={15} />
                          Cancel Deployment
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="premium-progress-body">
                <div className="premium-progress-steps">
                  {[
                    { label: 'Fetching Code', stepIdx: 0, key: 'fetching' },
                    { label: 'Environment', stepIdx: 1, key: 'checking' },
                    { label: 'Building', stepIdx: 2, key: 'building' },
                    { label: 'Deploying', stepIdx: 3, key: 'deploying' },
                  ].map((s, i) => {
                    const backendMilestone = (deploymentSession?.milestones || []).find(m => m.key === s.key);
                    const isDone = backendMilestone ? backendMilestone.status === 'completed' : deployStep > s.stepIdx;
                    const isActive = backendMilestone ? backendMilestone.status === 'running' : (deployStep === s.stepIdx && !deployError);
                    const isFailed = backendMilestone ? backendMilestone.status === 'failed' : (deployStep === s.stepIdx && !!deployError);
                    const statusMessage = backendMilestone?.message || '';

                    return (
                      <div key={i} className={`premium-step-item ${isDone ? 'done' : ''} ${isActive ? 'active' : ''} ${isFailed ? 'failed' : ''}`}>
                        <div className="premium-step-icon">
                          {isDone ? <Check size={18} strokeWidth={3} /> : isFailed ? <X size={18} strokeWidth={3} /> : isActive ? <LoaderCircle size={18} className="deploy-spin" /> : <Clock size={16} />}
                        </div>
                        <div className="premium-step-content">
                          <div className="premium-step-title">{s.label}</div>
                          <div className="premium-step-meta">
                            {isActive ? statusMessage || 'In progress...' : isFailed ? statusMessage || 'Failed' : isDone ? statusMessage || 'Completed' : 'Pending'}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="premium-progress-terminal">
                  <div className="premium-terminal-header">
                    <span>Build Logs</span>
                    <span>{fetchedRepoData?.appName || 'app'} • {fetchedRepoData?.branch || 'main'}</span>
                  </div>
                  <div className="premium-terminal-body">
                    {deployError && !deploymentSession?.logs?.length ? (
                      <span style={{ color: '#ef4444' }}>{deployError}</span>
                    ) : deploymentSession?.logs?.length > 0 ? (
                      deploymentSession.logs.map((l, idx) => (
                        <div key={idx} style={{ color: l.level === 'error' ? '#ef4444' : l.level === 'warn' ? '#f59e0b' : '#10b981' }}>
                          {l.message}
                        </div>
                      ))
                    ) : (
                      <span style={{ color: '#666' }}>Workspace opened — waiting for container logs...</span>
                    )}
                  </div>
                </div>
                      </div>
                </div>
              )}
            </div>
        )}

    </>
  );
}
